import React, { useState } from 'react';
import { Panel, Button, useToaster, Notification, Modal } from 'rsuite';
import { createActivity } from '../dataconnect-generated/js/default-connector';
import { useActiveArtists, useEventById } from '../contexts/ArtistContext';

const MultiFlightModal = ({ open, onClose, onSuccess, saveAsDraft = false }) => {
    // Auto-detect eventId from URL (works inside /event/:id/* routes)
    const eventMatch = window.location.pathname.match(/\/event\/([^/]+)/);
    const eventId = eventMatch ? eventMatch[1] : null;

    // Inside an event the flights belong to that event's artist; elsewhere the user picks one of their artists.
    // Placeholder data is the previously loaded event, so it must not decide the artist.
    const { data: event, isPlaceholderData } = useEventById(open ? eventId : null);
    const { data: activeArtists } = useActiveArtists();
    const [selectedArtistId, setSelectedArtistId] = useState('');
    const selectedArtist = activeArtists?.find(a => a.id === selectedArtistId) || activeArtists?.[0];
    const artistId = eventId ? (isPlaceholderData ? undefined : event?.artist?.id) : selectedArtist?.id;

    const [legs, setLegs] = useState([{ flightNumber: '', airline: '', startTime: '' }]);
    const toaster = useToaster();

    const handleAddLeg = () => {
        setLegs([...legs, { flightNumber: '', airline: '', startTime: '' }]);
    };

    const handleLegChange = (index, field, value) => {
        const newLegs = [...legs];
        newLegs[index][field] = value;
        setLegs(newLegs);
    };

    const handleSubmit = async () => {
        if (!artistId) {
            toaster.push(<Notification type="error" header="Feil">Fant ingen artist å knytte flightene til.</Notification>, { placement: 'topEnd' });
            return;
        }

        try {
            for (let i = 0; i < legs.length; i++) {
                const leg = legs[i];
                if (!leg.flightNumber) continue;
                
                // CreateActivity rejects variables it doesn't declare (e.g. userId)
                await createActivity({
                    artistId: artistId,
                    eventId: eventId,
                    name: `Flight ${leg.flightNumber}`,
                    startTimeTs: leg.startTime ? new Date(leg.startTime).toISOString() : new Date().toISOString(),
                    startTimeZone: "Europe/Oslo",
                    type: "FLIGHT",
                    flightNumber: leg.flightNumber,
                    airline: leg.airline,
                    status: saveAsDraft ? "DRAFT" : "PLANNED",
                    description: `Manuell innlagt flight leg ${i + 1} av ${legs.length}`
                });
            }
            toaster.push(<Notification type="success" header="Suksess">Opprettet {legs.length} flights!</Notification>, { placement: 'topEnd' });
            if (onSuccess) onSuccess(artistId);
        } catch (e) {
            console.error(e);
            toaster.push(<Notification type="error" header="Feil">Kunne ikke opprette flights.</Notification>, { placement: 'topEnd' });
        }
    };

    return (
        <Modal open={open} onClose={onClose} size="md">
            <Modal.Header>
                <Modal.Title>Legg til Multi-Flight</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ color: 'var(--rs-text-primary, #111)' }}>
                <p style={{ marginBottom: '15px', color: 'var(--rs-text-secondary, #666)' }}>
                    Legg til flere relaterte flyvninger om gangen (f.eks. mellomlandinger). {saveAsDraft ? "De havner som utkast i innboksen." : "De legges direkte inn i kalenderen som planlagt."}
                </p>
                {!eventId && activeArtists?.length > 1 && (
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '4px', color: 'var(--rs-text-primary, #111)' }}>Artist</label>
                        <select className="rs-input" value={artistId} onChange={e => setSelectedArtistId(e.target.value)}>
                            {activeArtists.map(artist => (
                                <option key={artist.id} value={artist.id}>{artist.name}</option>
                            ))}
                        </select>
                    </div>
                )}
                {legs.map((leg, index) => (
                    <Panel bordered key={index} style={{ marginBottom: '10px' }}>
                        <h5 style={{ color: 'var(--rs-text-primary, #111)' }}>Leg {index + 1}</h5>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', marginBottom: '4px', color: 'var(--rs-text-primary, #111)' }}>Flyselskap</label>
                                <input className="rs-input" value={leg.airline} onChange={e => handleLegChange(index, 'airline', e.target.value)} placeholder="F.eks. SAS" />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', marginBottom: '4px', color: 'var(--rs-text-primary, #111)' }}>Rutenummer</label>
                                <input className="rs-input" value={leg.flightNumber} onChange={e => handleLegChange(index, 'flightNumber', e.target.value)} placeholder="F.eks. SK123" />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', marginBottom: '4px', color: 'var(--rs-text-primary, #111)' }}>Avgangstid</label>
                                <input type="datetime-local" className="rs-input" value={leg.startTime} onChange={e => handleLegChange(index, 'startTime', e.target.value)} />
                            </div>
                        </div>
                    </Panel>
                ))}
                <Button appearance="subtle" onClick={handleAddLeg} style={{ marginTop: '10px' }}>+ Legg til ny leg</Button>
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={onClose} appearance="subtle">Avbryt</Button>
                <Button onClick={handleSubmit} appearance="primary">Lagre</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default MultiFlightModal;
